import React, { useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { kernels, silvermanBandwidth, kdeEstimator, makeGrid } from "./kernels";

// ---- Utility functions ----
function trapezoidalArea(points) {
  let area = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i][0] - points[i - 1][0];
    area += 0.5 * dx * (points[i][1] + points[i - 1][1]);
  }
  return area;
}

function normalizeCurve(points) {
  const area = trapezoidalArea(points);
  if (area <= 0) return points;
  return points.map(([x, y]) => [x, y / area]);
}

export default function KDEPlot({
  datasets = [{ data: [], color: "steelblue", label: "A" }],
  kernel = "epanechnikov",
  width = 256,
  height = 256,
  margin = { top: 30, right: 20, bottom: 40, left: 50 },
  axisFontSize = 14,
  fillCurves = false, // set true for area fill
  fillOpacity = 0.2,
  title = null,
  xticks = 6, // control how many x-ticks
}) {
  // Build evaluation grid across all datasets
  const all = datasets.flatMap((d) => d.data);
  const X = useMemo(() => makeGrid(all, 200, 0.05), [all]);

  const curves = useMemo(() => {
    const K = kernels[kernel];
    return datasets.map(({ data, color, label }) => {
      const h = silvermanBandwidth(data);
      const pts = kdeEstimator(K, h, X, data);
      const normPts = normalizeCurve(pts);
      return { pts: normPts, color, label };
    });
  }, [datasets, X, kernel]);

  // Scales
  const x = d3
    .scaleLinear()
    .domain(d3.extent(X))
    .range([margin.left, width - margin.right]);

  const yMax = d3.max(curves, (c) => d3.max(c.pts, (d) => d[1])) || 1;
  const y = d3
    .scaleLinear()
    .domain([0, yMax])
    .nice()
    .range([height - margin.bottom, margin.top]);

  // Line/area generators
  const line = d3
    .line()
    .x((d) => x(d[0]))
    .y((d) => y(d[1]))
    .curve(d3.curveBasis);

  const area = d3
    .area()
    .x((d) => x(d[0]))
    .y0(() => y(0))
    .y1((d) => y(d[1]))
    .curve(d3.curveBasis);

  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // {x,y,html}

  return (
    <div ref={containerRef} style={{ position: "relative", width, height }}>
      <svg width={width} height={height}>
        {/* Title */}
        {title && (
          <text
            x={width / 2}
            y={margin.top / 2}
            textAnchor="middle"
            fontSize={axisFontSize + 2}
            fontWeight="bold"
            fill="white"
          >
            {title}
          </text>
        )}

        {/* Curves */}
        {curves.map((c, i) => (
          <g key={i}>
            {fillCurves && (
              <path
                d={area(c.pts)}
                fill={c.color}
                fillOpacity={fillOpacity}
                stroke="none"
              />
            )}
            <path
              d={line(c.pts)}
              fill="none"
              stroke={c.color}
              strokeWidth={2}
            />
          </g>
        ))}

        {/* X axis */}
        <g
          className="x-axis"
          transform={`translate(0,${height - margin.bottom})`}
          ref={(node) =>
            d3.select(node).call(
              d3
                .axisBottom(x)
                .ticks(xticks) // limit number of ticks
                .tickSizeOuter(0)
                .tickFormat(d3.format("~s"))
            )
          }
        />

        {/* Y axis */}
        <g
          className="y-axis"
          transform={`translate(${margin.left},0)`}
          ref={(node) =>
            d3
              .select(node)
              .call(
                d3
                  .axisLeft(y)
                  .ticks(6)
                  .tickSizeOuter(0)
                  .tickFormat(d3.format("~s"))
              )
          }
        />

        {/* Style axes font */}
        <style>{`
        .x-axis text, .y-axis text {
          font-size: ${axisFontSize}px;
          font-family: sans-serif;        
        }
      `}</style>
      </svg>
    </div>
  );
}
